"use client";

import { useActionState } from "react";
import { subscribeNewsletter, type NewsletterState } from "@/app/actions/newsletter";

const initial: NewsletterState = { ok: false, message: "" };

type Props = {
  /** "compact" (default) matches the footer's inline signup; "hero" is the
   *  larger centered treatment used on the homepage's dedicated newsletter
   *  section. */
  size?: "compact" | "hero";
  source?: string;
};

export function NewsletterForm({ size = "compact", source = "footer" }: Props) {
  const [state, formAction, pending] = useActionState(subscribeNewsletter, initial);
  const hero = size === "hero";

  if (state.ok) {
    // "hero" renders on the homepage's own (theme-reactive) surface, so
    // --accent (navy) reads fine there; "compact" renders in SiteFooter,
    // which is permanently dark navy — --accent would be invisible there,
    // so it needs the always-vivid --cta instead.
    return (
      <p className={hero ? "m-0 text-[1.02rem] text-[var(--accent)]" : "m-0 text-sm text-[var(--cta)]"} role="status">
        {state.message}
      </p>
    );
  }

  return (
    <form action={formAction} className={hero ? "mx-auto mt-6 flex max-w-[440px] flex-col gap-2.5" : "flex flex-col gap-2"}>
      <input type="hidden" name="source" value={source} readOnly />
      <label className="hidden">
        Company
        <input name="company" tabIndex={-1} autoComplete="off" />
      </label>
      <div className={hero ? "flex gap-2.5" : "flex gap-2"}>
        <input
          type="email"
          name="email"
          required
          placeholder="Your email"
          aria-label="Email address"
          className={
            hero
              ? "h-12 flex-1 rounded border border-[var(--border-subtle)] bg-[var(--surface)] px-3.5 text-sm outline-none focus:border-[var(--accent)]"
              : "pbd-pf h-[42px] flex-1 text-[13px]"
          }
        />
        <button
          type="submit"
          disabled={pending}
          className={
            hero
              ? "shrink-0 cursor-pointer rounded border-0 bg-[var(--cta)] px-[22px] text-sm font-bold text-[#2b2b2b] hover:bg-[var(--cta-bright)] disabled:opacity-60"
              : "shrink-0 cursor-pointer rounded border-0 bg-[var(--cta)] px-4 text-[13px] font-bold text-[#2b2b2b] disabled:opacity-60"
          }
        >
          {pending ? "…" : hero ? "Subscribe" : "Join"}
        </button>
      </div>
      {state.message && !state.ok ? (
        <p className={hero ? "m-0 text-sm text-red-400" : "m-0 text-sm text-red-400"} role="alert">
          {state.message}
        </p>
      ) : null}
    </form>
  );
}
